import { addLog } from 'assets/components/feedback/Feedback';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset, HttpStatus } from 'config/Api.Config';
import ContractRefundInvoiceEmailTemplate from 'models/core/contractRefundInvoiceEmailTemplate/Model.ContractRefundInvoiceEmailTemplate';
import User from 'models/core/user/Model.User';
import ContractRefundInvoiceStatusTriggers from 'models/enums/ContractRefundInvoiceStatusTriggers';
import contractRefundInvoiceGenerateInvoiceDocumentApi from 'models/sales/contractRefundInvoice/generateInvoiceDocument/Api.ContractRefundInvoice.GenerateInvoiceDocument';
import ContractRefundInvoice from 'models/sales/contractRefundInvoice/Model.ContractRefundInvoice';
import contractRefundInvoiceEmailSendEmailApi from 'models/sales/contractRefundInvoiceEmail/sendEmail/Api.ContractRefundInvoiceEmail.SendEmail';
import { useState } from 'react';
import { salesContractRefundInvoiceIndexRoute } from './Sales.ContractRefundInvoice.Index';

export enum ContractRefundInvoiceAction {
  INFO = 'info',
  SEND_EMAIL = 'sendEmail',
}
export type VerifyContractRefundInvoiceAction = Utils.VerifyExtends<
  Module.Sales.ContractRefundInvoice.Actions,
  Utils.ValueOf<typeof ContractRefundInvoiceAction>
>;
export function useContractRefundInvoiceRouter() {
  return usePageRouter<Module.Sales.ContractRefundInvoice.Params, Module.Sales.ContractRefundInvoice.Query>({
    route: salesContractRefundInvoiceIndexRoute,
  });
}
export default function useContractRefundInvoice() {
  const { lang } = getLocales();
  const pageRouter = useContractRefundInvoiceRouter();
  const { permissions } = useAuthStore();

  const canView = permissions.VIEW_CONTRACTREFUNDINVOICE;

  //#region Api calls
  const listApi = useApi(
    {
      action: ContractRefundInvoice.list,
      default: {
        limit: defaultLimit,
        offset: defaultOffset,
      },
      body: {
        ...pageRouter.query.list,
      },
      callback: (req) => {
        pageRouter.updateQuery({ ...pageRouter.query, list: req });
      },
      wait: !canView,
    },
    [JSON.stringify(pageRouter.query.list)]
  );

  const findApi = useApi(
    {
      action: ContractRefundInvoice.find,
      wait: !canView || !pageRouter.params.id,
      body: {
        id: Number(pageRouter.params.id),
      },
    },
    [pageRouter.params.id]
  );
  //#endregion

  const reload = () => {
    if (pageRouter.params.id) findApi.execute((b) => b);
    listApi.execute((b) => b);
  };

  const userApi = useApi({ action: User.list, body: { isDisabled: false }, wait: true });

  const emailTemplatesApi = useApi({
    action: ContractRefundInvoiceEmailTemplate.list,
    body: {
      contractRefundInvoiceStatusTrigger: [ContractRefundInvoiceStatusTriggers.CONTRACT_REFUND_INVOICE_CREATED],
    },
  });
  const emailTemplates = emailTemplatesApi.payload?.data;

  async function sendEmail(emailTemplate: Model.IContractRefundInvoiceEmailTemplate) {
    const res = await contractRefundInvoiceEmailSendEmailApi({
      params: { contractRefundInvoiceId: Number(pageRouter.params.id) },
      body: emailTemplate,
    });

    if (res.status === HttpStatus.ok) {
      addLog({ success: lang.emailSuccessfullySent });
      reload();
      pageRouter.updateParams({});
    } else {
      addLog({ error: res.message || lang.error });
    }
  }

  const [generatingDocumentIds, setGeneratingDocumentIds] = useState<number[]>([]);
  const isGeneratingDocument = (contractRefundInvoiceId: number) =>
    generatingDocumentIds.includes(contractRefundInvoiceId);

  async function generateInvoiceDocument(contractRefundInvoiceId: number) {
    setGeneratingDocumentIds((prev) => [...prev, contractRefundInvoiceId]);
    try {
      const res = await contractRefundInvoiceGenerateInvoiceDocumentApi({ contractRefundInvoiceId });

      if (res.status === HttpStatus.ok) {
        addLog({ success: lang.invoiceSuccessfullyGenerated });
        reload();
      } else {
        addLog({ error: res.message || lang.error });
      }
    } finally {
      setGeneratingDocumentIds((prev) => prev.filter((id) => id !== contractRefundInvoiceId));
    }
  }

  return {
    listApi,
    findApi,
    userApi,
    pageRouter,
    permissions,
    selected: pageRouter.params.id ? findApi?.payload : null,
    canView,
    reload,
    generateInvoiceDocument,
    isGeneratingDocument,
    emailTemplates,
    emailTemplatesApi,
    sendEmail,
  };
}

export function useContractRefundInvoiceProvider() {
  return useDataProvider<ReturnType<typeof useContractRefundInvoice>>();
}
